import { FC, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppBar, Box, IconButton, Link, Toolbar, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import type { AppBarProps } from '@mui/material';
import AccountPopover from './AccountPopover';
import Logo from '@/components/Logo';
import ServicesMenu from './ServicesMenu';
import LightMode from '@mui/icons-material/LightMode'
import DarkMode from '@mui/icons-material/DarkMode'
import useSettings from '@/hooks/useSettings';
import { useEffect } from 'react';
import { THEMES } from '@/constants';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import SettingsMenu from '@/components/settings/SettingsMenu';
import { useIsSecureFileOnly } from '@/authProvider/useIsSecureFileOnly';
import { useIsLocalUserWithNoPhone } from '@/authProvider/useIsLocalUserWithNoPhone';
import { useHasFeature } from "@/utils/useHasFeature";



const DashboardNavbarRoot = styled(AppBar)(
  ({ theme }) => (
    {
      ...(
        theme.palette.mode === 'light' && {
          backgroundColor: theme.palette.primary.main,
          boxShadow: 'none',
          color: theme.palette.primary.contrastText
        }
      ),
      ...(
        theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none'
        }
      ),
      // zIndex: theme.zIndex.drawer + 100
    }
  )
);

const DashboardNavbar: FC<AppBarProps> = (props) => {
  const { ...other } = props;
  const theme = useTheme()
  const hidden = useMediaQuery(theme.breakpoints.up('lg'))
  const { settings, saveSettings } = useSettings()
  const settingsRef = useRef(settings)
  useEffect(() => { settingsRef.current = settings }, [settings])

  const isSecureFileOnly = useIsSecureFileOnly()
  const isLocalUserWithNoPhone = useIsLocalUserWithNoPhone()
  const hasFeature = useHasFeature()
  const hideReleaseDownload = !hasFeature.OnDataSecureDB && !hasFeature.OnDataSecureFile

  const toggleThemeMode = () => {
    saveSettings({
      ...settingsRef.current,
      theme: settingsRef.current.theme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK,
    })
  }

  const dispatch = useDispatch()
  const gotoDocs = () => {
    dispatch(push('/documents'))
  }
  const gotoRelease = () => {
    dispatch(push('/release/encryptiontool'))
  }

  return (
    <DashboardNavbarRoot {...other}>
      <Toolbar sx={{ minHeight: 64 }}>
        <Link component={RouterLink} sx={{ '&:focus': { border: '2px white solid', m: '-2px' }}}
          to={
              isSecureFileOnly       ? '/profile/edit'
            : isLocalUserWithNoPhone ? '/profile/create'
            : "/" } 
        >
          <Logo
            sx={{
              height: 40,
            }}
          />
        </Link>
        <Box sx={{ ml: 2 }}>
          {!(isSecureFileOnly || isLocalUserWithNoPhone) && <ServicesMenu />}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            ml: 2
          }}
        />
        {!(isSecureFileOnly || isLocalUserWithNoPhone) && <SettingsMenu />}
        <Box sx={{ ml: 1 }}>
          <Tooltip title={"Documentation"}>
            <IconButton onClick={gotoDocs} size="small">
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
        {!(isSecureFileOnly || isLocalUserWithNoPhone || hideReleaseDownload) && 
          <Box sx={{ ml: 1 }}>
            <Tooltip title="Product Releases">
              <IconButton onClick={gotoRelease} size="small">
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          </Box>}
        <Box sx={{ ml: 1 }}>
          <Tooltip
            title={theme.palette.mode === 'light' ? 'Light Mode' : 'Dark Mode'}
          >
            <IconButton onClick={toggleThemeMode} size="small">
              {theme.palette.mode === 'light' ?
                <LightMode /> :
                <DarkMode />}
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ ml: 2 }}>
          <AccountPopover />
        </Box>
      </Toolbar>
    </DashboardNavbarRoot>
  );
};

export default DashboardNavbar;
